import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, RotateCcw, Check, AlertTriangle, Database, FileText, Printer, Eye, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API, apiError } from "@/lib/api";
import { Loading, EmptyState, Badge, Btn, PageHeader, Panel, Select } from "@/components/common";

export default function ImportExport() {
  const { workspaceId, canEdit, isOwner } = useAuth();
  const [batches, setBatches] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const { data } = await api.get(`/import/batches?workspace_id=${workspaceId}`);
    setBatches(data);
  }, [workspaceId]);
  useEffect(() => { setBatches(null); load(); }, [load]);

  const dl = (path, name) => {
    api.get(path, { responseType: "blob" }).then((res) => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Download failed"));
  };

  const onFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/import/preview?workspace_id=${workspaceId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    ev.target.value = "";
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const rows = preview.rows.filter((r) => r.errors.length === 0).map((r) => r.data);
      const { data } = await api.post(`/import/commit?workspace_id=${workspaceId}`, { rows, source_attribution: "CSV import", skip_duplicates: true });
      toast.success(`Imported ${data.created_count} · skipped ${data.skipped_count} · failed ${data.failed_count}`);
      setPreview(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  const rollback = async (id) => {
    if (!window.confirm("Roll back this import? Records still tied to this batch and untouched since import will be removed. Subsequent unrelated edits are preserved.")) return;
    try {
      const { data } = await api.post(`/import/rollback/${id}?workspace_id=${workspaceId}`);
      toast.success(`Rolled back — removed ${data.removed}, retained ${data.retained}`); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const validRows = preview ? preview.rows.filter((r) => r.errors.length === 0).length : 0;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <PageHeader title="Imports & Exports" sub="CSV import with mapping, validation, preview, and safe rollback. Exports obey permissions." />

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Panel className="p-5">
          <div className="flex items-center gap-2 mb-3"><Upload size={16} strokeWidth={1.5} style={{ color: "var(--si-teal-light)" }} /><span className="font-medium" style={{ color: "var(--si-text)" }}>Import entities (CSV)</span></div>
          <p className="text-sm mb-4" style={{ color: "var(--si-text-2)" }}>Columns: name, type, aliases, description, tags, address, lat, lng, websites, verification_status, relationship_stage, notes.</p>
          <div className="flex gap-2 flex-wrap">
            <Btn variant="secondary" data-testid="dl-template" onClick={() => dl(`/import/template.csv?workspace_id=${workspaceId}`, "entity_template.csv")}><FileSpreadsheet size={14} /> Download template</Btn>
            {canEdit && <>
              <input ref={fileRef} type="file" accept=".csv" onChange={onFile} className="hidden" data-testid="import-file" />
              <Btn variant="primary" onClick={() => fileRef.current?.click()} data-testid="upload-csv"><Upload size={14} /> Upload CSV</Btn>
            </>}
          </div>
        </Panel>

        <Panel className="p-5">
          <div className="flex items-center gap-2 mb-3"><Download size={16} strokeWidth={1.5} style={{ color: "var(--si-teal-light)" }} /><span className="font-medium" style={{ color: "var(--si-text)" }}>Export</span></div>
          <p className="text-sm mb-4" style={{ color: "var(--si-text-2)" }}>Download entity records or the full structured workspace (origins, entities, memberships, relationships, evidence, activities).</p>
          <div className="flex gap-2 flex-wrap">
            <Btn variant="secondary" data-testid="export-csv" onClick={() => dl(`/export/entities.csv?workspace_id=${workspaceId}`, "entities_export.csv")}><Download size={14} /> Entities CSV</Btn>
            <Btn variant="secondary" data-testid="export-json" onClick={() => dl(`/export/workspace.json?workspace_id=${workspaceId}`, "workspace_export.json")}><Database size={14} /> Workspace JSON</Btn>
          </div>
        </Panel>
      </div>

      <WorkspaceDocuments workspaceId={workspaceId} isOwner={isOwner} />

      {preview && (
        <Panel className="mb-8">
          <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
            <div className="si-label">Preview · {preview.total} rows · {validRows} valid</div>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={() => setPreview(null)}>Cancel</Btn>
              <Btn variant="primary" onClick={commit} disabled={committing || validRows === 0} data-testid="commit-import"><Check size={14} /> {committing ? "Importing…" : `Import ${validRows} rows`}</Btn>
            </div>
          </div>
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0" style={{ background: "var(--si-surface)" }}>
                <tr style={{ borderBottom: "1px solid var(--si-border)" }}>
                  {["#", "name", "type", "status"].map((h) => <th key={h} className="text-left px-3 py-2 si-label font-normal">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row} style={{ borderBottom: "1px solid var(--si-border)" }}>
                    <td className="px-3 py-1.5 font-mono text-xs" style={{ color: "var(--si-text-2)" }}>{r.row}</td>
                    <td className="px-3 py-1.5" style={{ color: "var(--si-text)" }}>{r.data.name}</td>
                    <td className="px-3 py-1.5 font-mono text-xs" style={{ color: "var(--si-text-2)" }}>{r.data.type}</td>
                    <td className="px-3 py-1.5">
                      {r.errors.length > 0 ? <span className="text-xs flex items-center gap-1" style={{ color: "var(--si-danger)" }}><AlertTriangle size={12} /> {r.errors.join(", ")}</span>
                        : r.duplicate_of ? <span className="text-xs" style={{ color: "var(--si-warning)" }}>possible duplicate (will skip)</span>
                          : <span className="text-xs" style={{ color: "var(--si-success)" }}>ok</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <div className="si-label mb-3">Batch history</div>
      {!batches ? <Loading /> : batches.length === 0 ? (
        <EmptyState icon={FileSpreadsheet} title="No imports yet" hint="Upload a CSV to see batch history here." />
      ) : (
        <Panel>
          {batches.map((b) => (
            <div key={b.id} className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
              <div>
                <div className="text-sm font-mono" style={{ color: "var(--si-text)" }}>{b.id.slice(0, 8)} · {new Date(b.created_at).toLocaleString()}</div>
                <div className="si-label mt-0.5">created {b.created_count} · skipped {b.skipped_count} · failed {b.failed_count} · {b.source_attribution}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge value={b.status === "rolled_back" ? "disputed" : "confirmed"}>{b.status}</Badge>
                {canEdit && b.status !== "rolled_back" && <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => rollback(b.id)} data-testid={`rollback-${b.id}`}><RotateCcw size={13} /> Roll back</Btn>}
              </div>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}

function WorkspaceDocuments({ workspaceId, isOwner }) {
  const [manifest, setManifest] = useState(null);
  const [page, setPage] = useState("letter");
  const [per, setPer] = useState(25);
  const [restricted, setRestricted] = useState(false);
  const [appendix, setAppendix] = useState(true);
  const load = useCallback(() => {
    if (!workspaceId) return;
    api.get(`/reports/workspace/manifest?workspace_id=${workspaceId}&per=${per}${restricted ? "&include_restricted=true" : ""}`)
      .then((r) => setManifest(r.data)).catch(() => {});
  }, [workspaceId, per, restricted]);
  useEffect(() => { load(); }, [load]);
  const url = (volume, inline) => `${API}/reports/workspace.pdf?workspace_id=${workspaceId}&volume=${volume}&per=${per}&page=${page}&include_appendix=${appendix}${restricted ? "&include_restricted=true" : ""}`;
  const volumes = manifest?.volumes || 1;
  const printable = (manifest?.attachments || []).filter((a) => a.supported).length;
  const unsupported = (manifest?.attachments || []).length - printable;
  return (
    <Panel className="p-5 mb-8" data-testid="workspace-documents">
      <div className="flex items-center gap-2 mb-3"><Printer size={16} strokeWidth={1.5} style={{ color: "var(--si-teal-light)" }} /><span className="font-medium" style={{ color: "var(--si-text)" }}>Print documents</span></div>
      <p className="text-sm mb-4" style={{ color: "var(--si-text-2)" }}>
        Complete workspace dossier generated in numbered volumes. Includes identity, custom fields, personality, importance history, notes, relationships, memberships, groups, evidence, actions and stored correspondence. Preview opens in a new tab with your browser's print dialog.
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="block"><div className="si-label mb-1.5">Page size</div>
          <Select value={page} onChange={(e) => setPage(e.target.value)} style={{ width: 120 }} data-testid="doc-page-size"><option value="letter">Letter</option><option value="a4">A4</option></Select></label>
        <label className="block"><div className="si-label mb-1.5">Contacts / volume</div>
          <Select value={per} onChange={(e) => setPer(Number(e.target.value))} style={{ width: 120 }} data-testid="doc-per"><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option></Select></label>
        <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}><input type="checkbox" checked={appendix} onChange={(e) => setAppendix(e.target.checked)} data-testid="doc-appendix" /> Printable-attachment appendix</label>
        {isOwner && <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-warning)" }} data-testid="doc-restricted-label"><input type="checkbox" checked={restricted} onChange={(e) => setRestricted(e.target.checked)} data-testid="doc-restricted" /> <Lock size={13} /> Include restricted (PRIVATE — OWNER)</label>}
      </div>
      {manifest && (
        <div className="si-label mb-3">{manifest.total_entities} contacts · {volumes} volume{volumes > 1 ? "s" : ""} · {(manifest.attachments || []).length} attachment(s) in inventory ({printable} printable, {unsupported} unsupported — originals preserved in export)</div>
      )}
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: volumes }, (_, i) => i + 1).map((v) => (
          <div key={v} className="flex items-center gap-1" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
            <span className="si-label px-2">Vol {v}</span>
            <button className="px-2 py-1.5 text-xs flex items-center gap-1" style={{ color: "var(--si-teal-light)" }} onClick={() => window.open(url(v, true), "_blank")} data-testid={`doc-preview-${v}`}><Eye size={13} /> Preview / Print</button>
          </div>
        ))}
      </div>
    </Panel>
  );
}
